import { MessagesSquare, Pin, PinOff } from 'lucide-react'
import { BackButton } from 'primitives/back-button'
import { Typography } from 'primitives/typography'
import { Link, useLocation } from 'router-v6'
import { Button } from '../../primitives/button'
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '../../primitives/sidebar'
import { Tooltip } from '../../primitives/tooltip'
import type { AvatarMenuProps } from './components/footer'

interface AppSidebarConversationsProps {
  user?: AvatarMenuProps
  onSettingsClick?: () => void
  handleOrganizationClick: (org: NonNullable<AvatarMenuProps['organizations']>[number]) => void
  viewsData?: {
    favourites: Array<{ name: string; uuid: string }>
    publicViews: Array<{ name: string; uuid: string }>
    hasViews: boolean
    hasLiveConversationPermission: boolean
    favouriteView: (params: { uuid: string; favourite: boolean }) => void
  }
}

export const AppSidebarConversations: React.FC<AppSidebarConversationsProps> = ({ viewsData }) => {
  const location = useLocation()

  // Generate all sidebar items in the correct order (matching sub-nav.tsx logic)
  const generateSidebarItems = () => {
    const items = []

    // Find "All Conversations" in both favourites and publicViews (it could be in either)
    const allConversationsInFavourites = viewsData?.favourites?.find(
      (view) => view.name === 'All Conversations',
    )
    const allConversationsInPublic = viewsData?.publicViews?.find(
      (view) => view.name === 'All Conversations',
    )
    const allConversationsView = allConversationsInFavourites || allConversationsInPublic

    // If "All Conversations" exists, add All Conversations first, then Live Conversations
    if (allConversationsView) {
      // 1. All Conversations - always first
      items.push({
        title: allConversationsView.name,
        url: `/conversations/${allConversationsView.uuid}`,
        icon: MessagesSquare,
        uuid: allConversationsView.uuid,
        type: 'view',
        isPinned: Boolean(allConversationsInFavourites), // It's pinned if it's in favourites
        isPinnable: false, // All Conversations cannot be pinned/unpinned
      })

      // 2. Live Conversations (if user has permission) - comes AFTER All Conversations
      if (viewsData?.hasLiveConversationPermission) {
        items.push({
          title: 'Live Conversations',
          url: '/conversations/live-conversations',
          icon: MessagesSquare,
          type: 'static',
          isPinnable: false,
        })
      }
    } else {
      // Fallback: if no "All Conversations", just show Live Conversations if user has permission
      if (viewsData?.hasLiveConversationPermission) {
        items.push({
          title: 'Live Conversations',
          url: '/conversations/live-conversations',
          icon: MessagesSquare,
          type: 'static',
          isPinnable: false,
        })
      }
    }

    // Only add other views if hasViews is true
    if (!viewsData?.hasViews) {
      return items
    }

    // 3. Pinned Views (favorites, excluding All Conversations)
    const pinnedViews = viewsData.favourites
      .filter((view) => view.name !== 'All Conversations')
      .map((view) => ({
        title: view.name,
        url: `/conversations/${view.uuid}`,
        icon: MessagesSquare,
        uuid: view.uuid,
        type: 'pinned',
        isPinned: true,
        isPinnable: true,
      }))
    items.push(...pinnedViews)

    // 4. Regular Views (non-pinned, excluding All Conversations)
    const regularViews = viewsData.publicViews
      .filter(
        (view) =>
          view.name !== 'All Conversations' &&
          !viewsData.favourites.some((fav) => fav.uuid === view.uuid),
      )
      .map((view) => ({
        title: view.name,
        url: `/conversations/${view.uuid}`,
        icon: MessagesSquare,
        uuid: view.uuid,
        type: 'view',
        isPinned: false,
        isPinnable: true,
      }))
    items.push(...regularViews)

    return items
  }

  const handlePinClick = (e: React.MouseEvent, uuid: string, isPinned: boolean) => {
    e.preventDefault()
    e.stopPropagation()
    viewsData?.favouriteView({ uuid, favourite: !isPinned })
  }

  const sidebarItems = generateSidebarItems()

  return (
    <>
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-2">
          <BackButton data-testid="conversations-back-btn" />
          <Typography variant="P/600">Conversations</Typography>
        </div>
      </SidebarHeader>
      <SidebarContent className="gap-0">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {sidebarItems.map((item) => {
                const isActive = location.pathname.startsWith(item.url)
                return (
                  <SidebarMenuItem
                    key={
                      item.uuid
                        ? `${item.type}-${item.uuid}`
                        : `${item.type}-${item.title}-${item.url}`
                    }
                  >
                    <SidebarMenuButton isActive={isActive} asChild className="group">
                      <Link to={item.url}>
                        <item.icon className="w-4 h-4" strokeWidth={isActive ? 2 : 1.5} />
                        <Tooltip content={item.title}>
                          <span className="truncate flex-1">{item.title}</span>
                        </Tooltip>
                        {item.isPinnable && item.uuid && (
                          <Tooltip content={item.isPinned ? 'Unpin' : 'Pin to top'}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`ml-auto h-auto p-1 hover:bg-transparent ${
                                item.isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                              } transition-opacity`}
                              onClick={(e) => handlePinClick(e, item.uuid, item.isPinned || false)}
                              data-testid={`${item.isPinned ? 'unpin' : 'pin'}-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {item.isPinned ? (
                                <PinOff className="w-3 h-3 text-primary" strokeWidth={1.5} />
                              ) : (
                                <Pin className="w-3 h-3 text-muted-foreground" strokeWidth={1.5} />
                              )}
                            </Button>
                          </Tooltip>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </>
  )
}
